import jsPDF from 'jspdf';
import { toast } from 'react-hot-toast';
import { C, LOGO_URL, M, T, CONTENT_W, PAGE_H, FOOTER_SAFE, getEmpresa } from './theme.js';
import { totalServicio } from '../descuento.js';
import { HECHOS, estadoTrabajo as estadoPdf } from '../estadoTrabajo.js';
import { guardarPDF } from './entregar.js';

// Estado de cada trabajo: utils/estadoTrabajo.js (26-oct-2026; antes un trabajo sin cobrar salía "Rechazado")

const fmt = v => `$${Math.round(Number(v || 0)).toLocaleString('es-AR')}`;

// 3-nov-2026: "2026-10-31" con new Date() se lee como medianoche UTC y en Argentina daba el 30/10
function aFecha(f) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(f).slice(0, 10));
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(f);
}

function formatFecha(f) {
    if (!f) return '-';
    return aFecha(f).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function formatFechaLarga(f) {
    if (!f) return '-';
    return aFecha(f).toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' });
}

// Total con descuento: el que calcula el backend (s.totales), redondeado como siempre en este PDF
const calcTotal = s => Math.round(totalServicio(s));

// Trunca texto para que no exceda un ancho máximo
function truncar(doc, texto, maxW) {
    if (!texto) return '';
    if (doc.getTextWidth(texto) <= maxW) return texto;
    let t = texto;
    while (t.length > 0 && doc.getTextWidth(t + '…') > maxW) t = t.slice(0, -1);
    return t + '…';
}

function checkSalto(doc, y, necesita) {
    if (y + necesita > PAGE_H - FOOTER_SAFE) {
        doc.addPage();
        return 15;
    }
    return y;
}

// 3-nov-2026: rediseño. Un solo PDF que sirve para el cliente y para el admin: cada trabajo en una tarjeta con
// fecha, técnico, qué se hizo, repuestos, mano de obra y repuestos por separado, cómo pagó, garantía (90 días) y
// próximo service. Sin reparto con el técnico (eso está en la app) ni el teléfono del cliente.
const GARANTIA_DIAS = 90;
const MODALIDAD = { EFECTIVO_SIN_FACTURA: 'Efectivo', CON_FACTURA: 'Transferencia' };

const pagado = s => Math.round(Number(s.montoFinal) > 0 ? Number(s.montoFinal) : calcTotal(s));
const repuestosDe = s => (s.items || []).flatMap(it => it.repuestosUsados || []);
const totalRepuestos = s => Math.round(repuestosDe(s).reduce((a, r) => a + (Number(r.subtotal) || (Number(r.precio) || 0) * (Number(r.cantidad) || 1)), 0));
const nombresRepuestos = s => repuestosDe(s)
    .filter(r => r?.nombre && r.nombre.trim().toLowerCase() !== 'repuestos')
    .map(r => (Number(r.cantidad) > 1 ? `${r.cantidad}x ` : '') + r.nombre.trim()).join(', ');

function garantiaHasta(s) {
    const g = (s.items || []).map(it => it.garantiaHasta).filter(Boolean).sort().pop();
    if (g) return formatFecha(g);
    if (!s.fecha) return null;
    const d = aFecha(s.fecha);
    d.setDate(d.getDate() + GARANTIA_DIAS);
    return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function mesAnio(f) {
    if (!f) return null;
    const t = aFecha(f).toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
    return t.charAt(0).toUpperCase() + t.slice(1);
}

function pastilla(doc, texto, xDer, y, color) {
    doc.setFontSize(T.label);
    doc.setFont(undefined, 'bold');
    const w = doc.getTextWidth(texto) + 6;
    doc.setFillColor(...color);
    doc.roundedRect(xDer - w, y - 3.6, w, 5.4, 2.7, 2.7, 'F');
    doc.setTextColor(...C.white);
    doc.text(texto, xDer - w / 2, y, { align: 'center' });
}

export function generarPDFHistorialCliente({ cliente, servicios: todos }) {
    const loading = toast.loading('Generando PDF…');
    try {
    const servicios = (todos || []).filter(s => s.estado !== 'CANCELADO' && s.estado !== 'ARCHIVADO');
    const doc = new jsPDF();
    const pageW = doc.internal.pageSize.getWidth();
    const empresa = getEmpresa();
    const hoy = new Date().toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' });
    const X = M + 4, XD = pageW - M - 4, ANCHO = CONTENT_W - 8;

    // ── CABECERA ──
    doc.setFillColor(...C.navy);
    doc.rect(0, 0, pageW, 36, 'F');
    if (LOGO_URL) { try { doc.addImage(LOGO_URL, 'PNG', M, 5, 28, 11); } catch {} }
    doc.setFontSize(T.xl);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...C.white);
    doc.text('Historial de servicios', M, 29);
    doc.setFontSize(T.xs);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(220, 220, 222);
    doc.text(empresa.eslogan || 'Servicio técnico de dispensers', pageW - M, 19, { align: 'right' });
    doc.text(empresa.web || '', pageW - M, 24, { align: 'right' });
    doc.text(`Generado el ${hoy}`, pageW - M, 29, { align: 'right' });

    let y = 44;

    // ── CLIENTE ──
    const direccion = [cliente.calle, cliente.numero].filter(Boolean).join(' ')
        + (cliente.localidad ? `, ${cliente.localidad}` : '');
    const equipos = [];
    servicios.forEach(s => (s.items || []).forEach(it => {
        if (it.equipoSerial && it.equipoSerial !== 'MOSTRADOR' && !equipos.some(e => e.serie === it.equipoSerial))
            equipos.push({ serie: it.equipoSerial, modelo: it.equipoModelo });
    }));
    const cobrados = servicios.filter(s => s.estado === 'COBRADO' || s.estado === 'REALIZADO');
    const totalPagado = cobrados.reduce((a, s) => a + pagado(s), 0);
    const hechos = servicios.filter(s => HECHOS.includes(s.estado)).length;

    doc.setFontSize(T.xs);
    const textoEquipos = equipos.length
        ? doc.splitTextToSize('Equipos: ' + equipos.map(e => `${e.modelo || 'Dispenser'} · N/S ${e.serie}`).join('   '), ANCHO - 55)
        : [];
    const altoCli = 20 + textoEquipos.length * 4;
    doc.setFillColor(...C.grayBg);
    doc.setDrawColor(...C.grayBorder);
    doc.setLineWidth(0.3);
    doc.roundedRect(M, y, CONTENT_W, altoCli, 2.5, 2.5, 'FD');
    doc.setFontSize(T.lg);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...C.dark);
    doc.text(cliente.nombre || 'Cliente', X, y + 8);
    doc.setFontSize(T.xs);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(...C.grayText);
    if (direccion.trim()) doc.text(truncar(doc, direccion, ANCHO - 55), X, y + 13.5);
    if (textoEquipos.length) {
        doc.setFont(undefined, 'bold');
        doc.text(textoEquipos, X, y + 19);
    }
    doc.setFont(undefined, 'normal');
    doc.text(`${hechos} ${hechos === 1 ? 'trabajo' : 'trabajos'} · total pagado`, XD, y + 7.5, { align: 'right' });
    doc.setFontSize(T.xl);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...C.green);
    doc.text(fmt(totalPagado), XD, y + 15, { align: 'right' });
    y += altoCli + 6;

    // ── TRABAJOS ──
    servicios.forEach(s => {
        const hecho = HECHOS.includes(s.estado);
        const est = estadoPdf(s.estado);
        const varios = (s.items || []).length > 1;
        const queHizo = (s.items || []).map(it => {
            const t = String(it.trabajoRealizado || '').trim();
            return varios && it.equipoSerial && it.equipoSerial !== 'MOSTRADOR' ? `${it.equipoSerial}: ${t || 'sin detalle'}` : t;
        }).filter(Boolean);
        doc.setFontSize(T.sm);
        const lineasQue = doc.splitTextToSize(queHizo.length ? queHizo.join(varios ? '\n' : ' · ') : 'Sin detalle', ANCHO);
        const reps = nombresRepuestos(s);
        const lineasRep = reps ? doc.splitTextToSize(reps, ANCHO) : [];
        const total = pagado(s);
        const rep = Math.min(totalRepuestos(s), total);
        const enCierre = s.clienteTarifaMensual && total <= 0;
        const filas = enCierre ? 1 : (rep > 0 ? 3 : 1);
        const conCajas = hecho && (garantiaHasta(s) || s.proximoMantenimiento);
        const alto = 10 + 6 + lineasQue.length * 4.4 + (lineasRep.length ? 8 + lineasRep.length * 4.4 : 0)
            + 4 + filas * 6.5 + (conCajas ? 17 : 0) + 4;
        y = checkSalto(doc, y, alto);

        doc.setDrawColor(...C.grayBorder);
        doc.setLineWidth(0.3);
        doc.roundedRect(M, y, CONTENT_W, alto, 2.5, 2.5, 'S');
        doc.setFillColor(...C.navy);
        doc.roundedRect(M, y, CONTENT_W, 10, 2.5, 2.5, 'F');
        doc.rect(M, y + 5, CONTENT_W, 5, 'F');
        doc.setFontSize(T.sm);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(...C.white);
        const tecnico = (s.usuarioNombre || '').split(' ')[0];
        doc.text(truncar(doc, formatFechaLarga(s.fecha) + (tecnico ? ` · Técnico: ${tecnico}` : ''), ANCHO - 50), X, y + 6.5);
        const etiqueta = s.estado === 'COBRADO' && MODALIDAD[s.modalidadCobro]
            ? `${est.label} · ${MODALIDAD[s.modalidadCobro]}`.toUpperCase() : est.label.toUpperCase();
        pastilla(doc, etiqueta, XD, y + 6.3, C[est.color]);

        let yy = y + 16;
        const titulo = (t) => {
            doc.setFontSize(T.label);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(140, 140, 145);
            doc.text(t, X, yy);
            yy += 4.6;
        };
        titulo(hecho ? 'QUÉ SE HIZO' : 'QUÉ SE VA A HACER');
        doc.setFontSize(T.sm);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(...C.dark);
        doc.text(lineasQue, X, yy);
        yy += lineasQue.length * 4.4;
        if (lineasRep.length) {
            yy += 3.4;
            titulo(hecho ? 'REPUESTOS CAMBIADOS' : 'REPUESTOS');
            doc.setFontSize(T.sm);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(...C.dark);
            doc.text(lineasRep, X, yy);
            yy += lineasRep.length * 4.4;
        }
        yy += 1;

        const fila = (izq, der, fuerte) => {
            doc.setDrawColor(...(fuerte ? C.navy : C.grayBorder));
            doc.setLineWidth(fuerte ? 0.5 : 0.15);
            doc.line(X, yy, XD, yy);
            doc.setFontSize(fuerte ? T.md : T.sm);
            doc.setFont(undefined, fuerte ? 'bold' : 'normal');
            doc.setTextColor(...C.dark);
            doc.text(izq, X, yy + 4.6);
            doc.setFont(undefined, 'bold');
            doc.text(der, XD, yy + 4.6, { align: 'right' });
            yy += 6.5;
        };
        if (enCierre) fila('Total', 'Va en el cierre del mes', true);
        else {
            if (rep > 0) { fila('Mano de obra', fmt(total - rep)); fila('Repuestos', fmt(rep)); }
            fila(hecho ? 'Total' : 'Total presupuestado', fmt(total), true);
        }

        if (conCajas) {
            yy += 2;
            const cajas = [['Garantía', garantiaHasta(s) ? `Hasta el ${garantiaHasta(s)}` : null],
                ['Próximo service', mesAnio(s.proximoMantenimiento)]].filter(c => c[1]);
            const w = (ANCHO - 4 * (cajas.length - 1)) / cajas.length;
            cajas.forEach(([t, v], k) => {
                const cx = X + k * (w + 4);
                doc.setFillColor(...C.greenLight);
                doc.setDrawColor(187, 247, 208);
                doc.setLineWidth(0.3);
                doc.roundedRect(cx, yy, w, 13, 2, 2, 'FD');
                doc.setFontSize(T.xs);
                doc.setFont(undefined, 'bold');
                doc.setTextColor(...C.dark);
                doc.text(t, cx + 4, yy + 5);
                doc.setFont(undefined, 'normal');
                doc.text(v, cx + 4, yy + 10);
            });
        }
        y += alto + 5;
    });

    // ── PIE en todas las páginas ──
    const totalPags = doc.internal.getNumberOfPages();
    for (let p = 1; p <= totalPags; p++) {
        doc.setPage(p);
        const pH = doc.internal.pageSize.getHeight();
        doc.setDrawColor(...C.grayBorder);
        doc.setLineWidth(0.2);
        doc.line(M, pH - 12, pageW - M, pH - 12);
        doc.setFontSize(T.xs);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(...C.grayText);
        const wa = empresa.whatsapp ? ` al ${empresa.whatsapp}` : '';
        doc.text(`¿Algún problema con el equipo? Escribinos por WhatsApp${wa} · ${empresa.web || ''}`, pageW / 2, pH - 6.5, { align: 'center' });
        if (totalPags > 1) doc.text(`${p} / ${totalPags}`, pageW - M, pH - 6.5, { align: 'right' });
    }

    guardarPDF(doc, `historial_${(cliente.nombre || 'cliente').replace(/\s+/g, '_')}.pdf`);
    toast.success('PDF generado', { id: loading });
    } catch (e) {
        console.error('Error generando PDF historial:', e);
        toast.error('Error al generar el PDF', { id: loading });
    }
}
